import { QueryHookOptions, useQuery } from '@provision/graphql-client';
import { useMemo } from 'react';
import { GET_ENABLED_ASSIGNEE_GROUPS_QUERY } from 'src/graphql/entities/conversation-assignee-group/conversation-assignee-group.operations';
import type {
  ConversationAssigneeGroup,
  GetConversationAssigneeGroupsQuery,
} from 'src/graphql/entities/conversation-assignee-group/conversation-assignee-group.types';
import type { ConversationAssigneeGroupRaw } from 'src/types';
import { sortAlphabetically } from 'src/utils/sorting';

export const useConversationAssigneeGroups = (
  partnerId: number | null | undefined,
  options?: QueryHookOptions<GetConversationAssigneeGroupsQuery>,
) => {
  const { data, ...rest } = useQuery<GetConversationAssigneeGroupsQuery>(
    GET_ENABLED_ASSIGNEE_GROUPS_QUERY,
    {
      ...options,
      context: {
        omitIsolationModeHeader: true,
      },
      variables: {
        partnerId,
      },
    },
  );

  const [conversationAssigneeGroups, rawConversationAssigneeGroupsMap] = useMemo(() => {
    if (!data?.getEnabledConversationAssigneeGroups?.records?.length) {
      return [null];
    }

    return [
      data?.getEnabledConversationAssigneeGroups.records.reduce(
        (
          acc: Record<string, ConversationAssigneeGroup>,
          conversationAssigneeGroup: ConversationAssigneeGroupRaw,
        ) => {
          const { conversationAssigneeGroupId, name, icon, id, partnerId } =
            conversationAssigneeGroup;

          acc[conversationAssigneeGroupId] = {
            conversationAssigneeGroupId,
            name,
            icon,
            id,
            partnerId,
          };

          return acc;
        },
        {},
      ),
      new Map(
        data?.getEnabledConversationAssigneeGroups.records.map((i) => [
          i.conversationAssigneeGroupId,
          i,
        ]),
      ),
    ];
  }, [data?.getEnabledConversationAssigneeGroups]);

  return {
    rawConversationAssigneeGroupsMap,
    conversationAssigneeGroups,
    ...rest,
  };
};

export function useConversationAssigneeGroupsOptions(
  partnerId: number | null | undefined,
  options?: QueryHookOptions<GetConversationAssigneeGroupsQuery>,
) {
  const { conversationAssigneeGroups } = useConversationAssigneeGroups(partnerId, options);

  return useMemo(() => {
    if (!conversationAssigneeGroups) return [];
    return Object.keys(conversationAssigneeGroups)
      .map((conversationAssigneeGroupId) => ({
        id: conversationAssigneeGroupId,
        value: conversationAssigneeGroupId,
        label: conversationAssigneeGroups[conversationAssigneeGroupId].name,
        partnerId: conversationAssigneeGroups[conversationAssigneeGroupId].partnerId,
      }))
      .sort(sortAlphabetically('label'));
  }, [conversationAssigneeGroups]);
}
